import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  IAddress,
  BAddress,
  IContact,
  IPersonalInfo,
} from "../../../typings/models/PersonalInfo";
import Loader from "../../../components/molecules/Loader";
import {
  getAddress,
  getCAVSMaster,
  getLabMaster,
} from "../../../api/apimethods";
import { IBioPurposeOfTest } from "../../../typings/models/BioPurposeOfTest";
import { ISampleSeverity } from "../../../typings/models/SampleSeverity";
import { ITypeOfServices } from "../../../typings/models/TypeOfServices";
import { PersonalInformationProps } from "../SamplesAndTests";

export const initialPersonalInfo: IPersonalInfo = {
  customerType: "",
  customerName: "",
  companyName: "",
  customerNRIC: "",
  SameAsMailingAddress: true,
  modeOfPayment: "Credit Card",
  paymentBy: "Applicant",
  salutation: "",
  uen: "",
  contact: {
    mobileNumber: "",
    email: "",
    homeNumber: "",
    salutation: "",
    otherSalutation: "",
  },
  address: {
    postalCode: "",
    blockHouseNo: "",
    country: "",

    streetName: "",
    floorLevel: "",
    unit: "",
    buildingName: "",
  },
  billingAddress: {} as BAddress,
};
const PersonalInformation: React.FC<PersonalInformationProps> = ({
  profileInfo,
  onModeOfPaymentChange,
}) => {
  const handleShowModal = () => setShowModal(true);
  const location = useLocation();
  const handleCloseModal = () => setShowModal(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [contactErrors, setContactErrors] = useState<IContact>();
  const [mailingErrors, setMailingErrors] = useState<IAddress>();
  const [userPaymentMode, setUserPaymentMode] = useState<string>("");
  const [params, setParams] = useSearchParams();
  const [personalInfo, setPersonalInfo] =
    useState<IPersonalInfo>(initialPersonalInfo);
  const [address, setAddress] = useState<IAddress | undefined>();
  const [appDataAddressList, setAppDataAddressList] = useState<IAddress[]>([]);
  const [addressList, setAddressList] = useState<IAddress[]>([]);
  const [billingAddress, setBillingAddress] = useState<BAddress | undefined>();
  const [contact, setContact] = useState<IContact>();
  const [pageLoader, setPageLoader] = useState<boolean>(false);
  const [SameAsMailingAddress, setSameAsMailingAddress] = useState(false);
  const [isNewAddress, setIsNewAddress] = useState<boolean>(false);
  const [isBillingAddressModified, setIsBillingAddressModified] =
    useState(false);
  const [typeOfServiceOptions, setTypeOfServiceOptions] = useState<
    ITypeOfServices[]
  >([]);
  const [salutationOptions, setSalutationOptions] =
    useState<IBioPurposeOfTest[]>();
  const [uenValues, setUenValues] = useState<string[]>([]);
  const [BioSurveillanceValues, setBioSurveillanceValues] = useState<string[]>(
    []
  );
  const [customerNRIC, setCustomerNRIC] = useState<string>("");
  const type = params.get("type");
  const getUserInfo = localStorage.getItem("app-data");
  const userInfo = JSON.parse(getUserInfo || "");
  let UEN = userInfo?.user?.uen || null;
  let role = userInfo?.user?.role || "";
  let customerIdentifier = role === "CorpPass" ? UEN : customerNRIC;
  const handlePersonalInfoChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setUserInteracted(true);
    const { name, value, type } = e.target;

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      const { checked } = e.target;
      if (name === "SameAsMailingAddress") {
        setSameAsMailingAddress(checked);
        setPersonalInfo((prevState) => ({
          ...prevState,
          SameAsMailingAddress: checked,
        }));

        if (checked) {
          setBillingAddress(address);
        }
      }
    } else {
      setPersonalInfo((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      if (name === "modeOfPayment" && onModeOfPaymentChange) {
        onModeOfPaymentChange(value);
      }
    }
  };
  const validateForm = () => {
    const newContactErrors = {} as IContact;
    const newAddressErrors = {} as IAddress;
    if (!contact?.salutation) {
      newContactErrors.salutation = "Salutation is required.";
    }

    if (contact?.salutation === "Others" && !contact?.otherSalutation) {
      newContactErrors.otherSalutation = "Please specify other salutation.";
    }

    if (!contact?.email) {
      newContactErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      newContactErrors.email = "Please enter a valid email address.";
    }
    if (!address?.streetName) {
      newAddressErrors.streetName = "Street name is required.";
    }

    if (!address?.postalCode) {
      newAddressErrors.postalCode = "Postal code is required.";
    }
    setContactErrors(newContactErrors);
    setMailingErrors(newAddressErrors);
    const contactHasErrors = Object.keys(newContactErrors).length > 0;
    const addressHasErrors = Object.keys(newAddressErrors).length > 0;
    return !(contactHasErrors || addressHasErrors);
  };

  const handleUpdate = () => {
    if (validateForm()) {
      storeInLocalStorage();
      handleCloseModal();
    }
  };

  const handleContactChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setContact((prevState) => ({
      ...prevState!,
      [name]: value,
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setContact((prevState) => ({
      ...prevState!,
      [name]: value,
    }));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact((prevContact) => ({
      ...prevContact!,
      otherSalutation: e.target.value,
    }));
  };

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setAddress((prevState) => ({
      ...prevState!,
      [name]: value,
    }));

    if (SameAsMailingAddress) {
      setBillingAddress((prevState) => {
        return {
          ...prevState!,
          [name]: value,
        };
      });
      setIsBillingAddressModified(true);
    }
  };
  const handleBillingAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setBillingAddress((prevAddress) => {
      return {
        ...prevAddress!,
        [name]: value,
      };
    });
    setIsBillingAddressModified(true);
  };

  const handleCheckboxChangeEditMailingAddress = () => {
    setIsNewAddress(!isNewAddress);
  };

  useEffect(() => {
    if (profileInfo) {
      fetchAndSetMasterData(profileInfo);
    } else {
      fetchAndSetMasterData();
    }
  }, [profileInfo]);

  const fetchAndSetMasterData = async (profileInfo?: any) => {
    setPageLoader(true);

    try {
      await fetchLabMasterData();
      await fetchCAVSMasterData();
      await fetchUserData(profileInfo);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setPageLoader(false);
    }
  };
  const fetchLabMasterData = async () => {
    const response = await getLabMaster();
    if (response?.status !== 200) return;

    const { masters } = response.data;
    if (masters.typeOfService) {
      let typeOfService = masters.typeOfService;
      let values: number[] = Object.values(typeOfService);
      let typeOfServiceArr: ISampleSeverity[] = Object.keys(typeOfService).map(
        (x, ind) => {
          return {
            id: values[ind],
            name: x,
          };
        }
      );
      setTypeOfServiceOptions(typeOfServiceArr);
    }
    if (masters?.exchangeOfServices) {
      setUenValues(masters.exchangeOfServices.map((s: any) => s.uen));
    }
    if (masters?.bioSurveillances) {
      setBioSurveillanceValues(masters.bioSurveillances.map((s: any) => s.uen));
    }
  };
  const fetchCAVSMasterData = async () => {
    const response = await getCAVSMaster();
    if (response?.status !== 200) return;

    const salutations = response?.data?.bioMasters?.salutation;
    if (salutations) {
      const salutationOptions = Object.entries(salutations).map(
        ([name, id]) => ({
          id,
          name,
        })
      );
      setSalutationOptions(salutationOptions);
    }
  };
  const fetchUserData = async (profileInfo?: any) => {
    if (profileInfo?.customerNRIC) {
      return await processProfileData(profileInfo);
    }
    const storedProfile = localStorage.getItem("profile-info");
    if (storedProfile) {
      return await processProfileData(JSON.parse(storedProfile));
    }

    const storedUser = localStorage.getItem("app-data");
    if (storedUser) {
      return await processStoredUser(JSON.parse(storedUser));
    }
  };
  const processProfileData = async (profileInfo: any) => {
    setPersonalInfo(profileInfo);
    setContact(profileInfo.contact);

    const addressData = await fetchAddress(
      profileInfo.customerNRIC,
      profileInfo.contact.email,
      profileInfo.customerType
    );

    setCustomerNRIC(profileInfo.customerNRIC);
    setAddress(profileInfo.address || addressData);
    setBillingAddress(profileInfo.billingAddress || addressData);
    setAddressList(profileInfo.addressList || [addressData]);
  };
  const processStoredUser = async (userInfo: any) => {
    if (!userInfo?.user) return;

    const user = userInfo.user;
    setUserPaymentMode(user.modeOfPayment || "Credit Card");
    setCustomerNRIC(user.idNo || "");
    const contact = {
      mobileNumber: user.mobileNumber,
      email: user.email,
      homeNumber: user.contactNo,
      salutation: user.salutation,
      otherSalutation: "",
    };
    setContact(contact);
    const addressData = await fetchAddress(user.idNo, user.email, user.role);
    setAddressList(addressData || user.registeredAddresses);
    setAppDataAddressList(addressData || user.registeredAddresses);

    const defaultAddress = addressData?.[0] || user.registeredAddresses?.[0];

    setAddress(defaultAddress);
    setBillingAddress(defaultAddress);

    setPersonalInfo((prev) => ({
      ...prev,
      customerName: user.name || "",
      customerType: user.role || "",
      uen: user.uen || "",
      salutation: user.salutation || "",
      companyName: user.companyName || "",
      customerNRIC: user.idNo || "",
      modeOfPayment: user.modeOfPayment || "Credit Card",
      contact,
      address: defaultAddress,
    }));
  };

  const fetchAddress = async (idNo: string, email: string, role: string) => {
    const response = await getAddress(idNo, email, role);
    return response?.status === 200 ? response.data ?? [] : [];
  };

  const normalizedPathname = location.pathname
    .replace(/\/+$/, "")
    .replace(/#.*$/, "");
  const hiddenPaths = ["/review", "/cost-and-confirm", "/samples-and-tests"];
  const shouldHideFields = hiddenPaths.includes(normalizedPathname);
  useEffect(() => {
    const storedModeOfPayment = localStorage.getItem("modeOfPayment");

    if (storedModeOfPayment && storedModeOfPayment !== "Biosurveillance") {
      return;
    }
    if (!userInteracted) {
      if (
        BioSurveillanceValues.includes(customerNRIC) &&
        !shouldHideFields &&
        personalInfo.modeOfPayment !== "Biosurveillance"
      ) {
        setPersonalInfo((prevState) => ({
          ...prevState,
          modeOfPayment: "Biosurveillance",
        }));
      } else if (
        shouldHideFields &&
        ["Exchange of Service agreement", "Biosurveillance"].includes(
          personalInfo.modeOfPayment
        )
      ) {
        setPersonalInfo((prevState) => ({
          ...prevState,
          modeOfPayment: "Credit Card",
        }));
      }
    }
  }, [
    shouldHideFields,
    personalInfo.modeOfPayment,
    BioSurveillanceValues,
    customerNRIC,
    userInteracted,
  ]);

  const formatAddress = (address: IAddress): string => {
    return `${address.blockHouseNo}, ${address.buildingName},${address.country}, ${address.unit}, ${address.floorLevel}, ${address.streetName}, ${address.postalCode}`;
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIndex = parseInt(e.target.value, 10);

    const selectedValue = e.target.value;

    if (selectedValue === "new") {
      setAddress({
        blockHouseNo: "",
        buildingName: "",
        unit: "",
        floorLevel: "",
        streetName: "",
        postalCode: "",
        country: "",
      });
      setIsNewAddress(true);
    } else {
      const selectedAddress = appDataAddressList[selectedIndex];
      setAddress(selectedAddress);
      setPersonalInfo((prev) => ({
        ...prev,
        address: selectedAddress,
      }));
      setIsNewAddress(false);
    }
  };

  useEffect(() => {
    const getAppDataAddressList = () => {
      const storedAppData = localStorage.getItem("app-data");

      if (storedAppData) {
        const appData = JSON.parse(storedAppData);

        if (appData?.user) {
          const paymentMode = appData?.user?.modeOfPayment || "Credit Card";
          setUserPaymentMode(paymentMode);
        }

        if (
          appData?.user?.registeredAddresses &&
          appData.user.registeredAddresses.length > 0
        ) {
          const addresses = appData.user.registeredAddresses.map(
            (address: any) => ({
              postalCode: address.postalCode,
              country: address.country,
              blockHouseNo: address.blockHouseNo,
              streetName: address.streetName,
              floorLevel: address.floorLevel,
              unit: address.unit,
              buildingName: address.buildingName,
            })
          );

          setAppDataAddressList(addresses);
        } else {
          console.log("No registered addresses found.");
        }
      } else {
        console.log("No app-data found in localStorage.");
      }
    };

    getAppDataAddressList();
  }, []);
  useEffect(() => {
    storeInLocalStorage();
  }, [personalInfo]);
  const storeInLocalStorage = () => {
    if (
      personalInfo?.customerName &&
      personalInfo.contact &&
      personalInfo.address
    ) {
      const updatedPersonalInfo = { ...personalInfo };

      if (contact) {
        updatedPersonalInfo.contact = contact;
      }

      if (address) {
        updatedPersonalInfo.address = { ...address };
      }

      if (billingAddress) {
        updatedPersonalInfo.billingAddress = { ...billingAddress };
      }

      localStorage.setItem("modeOfPayment", personalInfo.modeOfPayment);
      localStorage.setItem("profile-info", JSON.stringify(updatedPersonalInfo));
    }
  };
  useEffect(() => {
    if (!userInteracted) {
      const storedModeOfPayment = personalInfo.modeOfPayment;
      if (storedModeOfPayment && onModeOfPaymentChange) {
        onModeOfPaymentChange(storedModeOfPayment);
      }
    }
  }, [personalInfo.modeOfPayment]);
  return (
    <>
      <div className="personal-info">
        <div className="info-heading">
          {type !== "acknowledgement" && (
            <a
              href="#"
              className="edit-icon float-end"
              onClick={handleShowModal}
            >
              <i className="fa fa-pencil-square-o"></i> Edit
            </a>
          )}
          <h2>Personal Information</h2>
        </div>

        {pageLoader ? (
          <Loader />
        ) : (
          <div className="info-content">
            <div className="row">
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-md-4">
                    <h6>
                      Salutation <span className="star-red">*</span>
                    </h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input
                        type="radio"
                        name="salutation"
                        value={contact?.salutation}
                      />
                    ) : (
                      <p>
                        <small>:</small>{" "}
                        {contact?.salutation === "Others"
                          ? contact?.otherSalutation
                          : contact?.salutation}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6>Customer Name</h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input
                        type="text"
                        value={personalInfo.customerName}
                        onChange={handlePersonalInfoChange}
                      />
                    ) : (
                      <p>
                        <small>:</small> {personalInfo.customerName}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6>
                      Email <span className="star-red">*</span>
                    </h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input
                        type="text"
                        value={contact?.email}
                        onChange={handleContactChange}
                      />
                    ) : (
                      <p>
                        <small>:</small> {contact?.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6>Mobile Number</h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input
                        type="text"
                        value={contact?.mobileNumber}
                        onChange={handleContactChange}
                      />
                    ) : (
                      <p>
                        <small>:</small> {contact?.mobileNumber}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6>Office/Home Number</h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input
                        type="text"
                        value={contact?.homeNumber}
                        onChange={handleContactChange}
                      />
                    ) : (
                      <p>
                        <small>:</small> {contact?.homeNumber}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6> Mailing Address</h6>
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Block No
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="blockHouseNo"
                          maxLength={10}
                          value={address?.blockHouseNo}
                          disabled
                        />
                      </div>

                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Street Name<span className="star-red">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="streetName"
                          value={address?.streetName}
                          disabled
                        />
                      </div>
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Floor Level
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="floorLevel"
                          maxLength={10}
                          value={address?.floorLevel}
                          disabled
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Unit
                        </label>

                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="unit"
                          maxLength={10}
                          value={address?.unit}
                          disabled
                        />
                      </div>
                      {address?.buildingName && (
                        <div className="col-md-12 pos-rel mt-2">
                          <label className="col-form-label" id="label-design">
                            Building Name
                          </label>
                          <input
                            type="text"
                            className="form-control personal-disabled"
                            name="buildingName"
                            value={address?.buildingName}
                            disabled
                          />
                        </div>
                      )}
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Country
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="country"
                          value={address?.country}
                          disabled
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Postal Code<span className="star-red">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="postalCode"
                          maxLength={10}
                          value={address?.postalCode}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row hidden">
                  <div className="col-md-4">
                    <h6>Customer NRIC</h6>
                  </div>
                  <div className="col-md-8">
                    <p>
                      <small>:</small> {personalInfo.customerNRIC?.slice(-4)}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <h6>Mode of Payment</h6>
                  </div>
                  <div className="col-md-8">
                    {isEditMode ? (
                      <input type="text" value={personalInfo.modeOfPayment} />
                    ) : (
                      <p>
                        <small>: {""}</small>
                        {shouldHideFields &&
                        [
                          "Exchange of Service agreement",
                          "Biosurveillance",
                        ].includes(personalInfo.modeOfPayment)
                          ? "Credit Card"
                          : personalInfo.modeOfPayment || "Credit Card"}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  {!shouldHideFields && (
                    <div>
                      <div className="row">
                        <div className="col-md-4">
                          <h6>Payment by</h6>
                        </div>
                        <div className="col-md-8">
                          {isEditMode ? (
                            <input
                              type="text"
                              value={personalInfo.paymentBy}
                              onChange={handlePersonalInfoChange}
                              name="paymentBy"
                            />
                          ) : (
                            <p>
                              <small>: </small>
                              {personalInfo.paymentBy || "Applicant"}
                            </p>
                          )}
                        </div>
                      </div>
                      {/*{personalInfo.paymentBy === "Third-party" && (
                        <>
                          <div className="row">
                            <div className="col-md-4">
                              <h6>Third-party NRIC/UEN</h6>
                            </div>
                            <div className="col-md-8">
                              {isEditMode ? (
                                <input
                                  type="text"
                                  value={personalInfo.thirdPartyNRIC}
                                  onChange={handlePersonalInfoChange}
                                  name="thirdPartyNRIC"
                                />
                              ) : (
                                <p>
                                  <small>:</small>
                                  {personalInfo.thirdPartyNRIC}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-4">
                              <h6>Third-party Email</h6>
                            </div>
                            <div className="col-md-8">
                              {isEditMode ? (
                                <input
                                  type="email"
                                  value={personalInfo.thirdPartyEmail}
                                  onChange={handlePersonalInfoChange}
                                  name="thirdPartyEmail"
                                />
                              ) : (
                                <p>
                                  <small>:</small>
                                  {personalInfo.thirdPartyEmail}
                                </p>
                              )}
                            </div>
                          </div>
                        </>
                      )} */}
                      {personalInfo.customerType != "CorpPass" && personalInfo.paymentBy === "Third-party" && (
                        <div className="row">
                          <div className="col-md-4">
                            <h6>Company Name</h6>
                          </div>
                          <div className="col-md-8">
                            {isEditMode ? (
                              <input type="text" value={personalInfo.companyName} />
                            ) : (
                              <p>
                                <small>: </small>
                                {personalInfo.companyName}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {personalInfo.customerType === "CorpPass" && (
                  <div className="row">
                    <div className="col-md-4">
                      <h6>Company Name</h6>
                    </div>
                    <div className="col-md-8">
                      {isEditMode ? (
                        <input type="text" value={personalInfo.companyName} />
                      ) : (
                        <p>
                          <small>: </small>
                          {personalInfo.companyName}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-md-4">
                    <h6> Billing Address</h6>
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Block No
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="blockHouseNo"
                          maxLength={10}
                          value={billingAddress?.blockHouseNo}
                          disabled
                        />
                      </div>

                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Street Name
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="streetName"
                          value={billingAddress?.streetName}
                          disabled
                        />
                      </div>
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Floor Level
                        </label>

                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="floorLevel"
                          maxLength={10}
                          value={billingAddress?.floorLevel}
                          disabled
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Unit
                        </label>

                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="unit"
                          maxLength={10}
                          value={billingAddress?.unit}
                          disabled
                        />
                      </div>
                      {billingAddress?.buildingName && (
                        <div className="col-md-12 pos-rel">
                          <label className="col-form-label" id="label-design">
                            Building Name
                          </label>
                          <input
                            type="text"
                            className="form-control personal-disabled"
                            name="buildingName"
                            value={billingAddress?.buildingName}
                            disabled
                          />
                        </div>
                      )}
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Country
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="country"
                          value={billingAddress?.country}
                          disabled
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          className="form-control personal-disabled"
                          name="postalCode"
                          maxLength={10}
                          value={billingAddress?.postalCode}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        backdrop="static"
        keyboard={false}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Personal Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="row mb-3">
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="Salutation" className="form-label">
                      Salutation<span className="star-red">*</span>
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <select
                      className={`form-select ${
                        contactErrors?.salutation ? "is-invalid" : ""
                      }`}
                      onChange={handleDropdownChange}
                      value={contact?.salutation}
                      name="salutation"
                    >
                      <option value=""></option>
                      {salutationOptions &&
                        salutationOptions.map((x) => (
                          <option key={x.name} value={x.name}>
                            {x.name}
                          </option>
                        ))}
                      <option value="Others">Others</option>
                    </select>
                    {contactErrors?.salutation && (
                      <div className="invalid-feedback">
                        {contactErrors?.salutation}
                      </div>
                    )}
                    {contact?.salutation === "Others" && (
                      <input
                        type="text"
                        className={`form-control mt-2 ${
                          contactErrors?.otherSalutation ? "is-invalid" : ""
                        }`}
                        onChange={handleTextChange}
                        value={contact?.otherSalutation}
                        maxLength={4}
                        placeholder="Enter other salutation"
                        name="othersalutation"
                      />
                    )}
                    {contactErrors?.otherSalutation && (
                      <div className="invalid-feedback">
                        {contactErrors?.otherSalutation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="nric" className="form-label">
                      Customer NRIC
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <input
                      type="text"
                      id="customerNRIC"
                      className="form-control"
                      value={personalInfo.customerNRIC?.slice(-4)}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="customerName" className="form-label">
                      Customer Name
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <input
                      type="text"
                      id="customerName"
                      className="form-control"
                      value={personalInfo.customerName}
                      onChange={handlePersonalInfoChange}
                      name="customerName"
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="homeNumber" className="form-label">
                      Office/Home Number
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <input
                      type="tel"
                      className="form-control"
                      onChange={handleContactChange}
                      name="homeNumber"
                      pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                      value={contact?.homeNumber}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="email" className="form-label">
                      Email<span className="star-red">*</span>
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <input
                      type="email"
                      id="email"
                      className={`form-control ${
                        contactErrors?.email ? "is-invalid" : ""
                      }`}
                      name="email"
                      value={contact?.email}
                      onChange={handleContactChange}
                    />
                    {contactErrors?.email && (
                      <div className="invalid-feedback">
                        {contactErrors.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row">
                  <div className="col-sm-5">
                    <label htmlFor="paymentMethod" className="form-label">
                      Mode of Payment
                    </label>
                  </div>
                  <div className="col-sm-7">
                    <select
                      className="form-select"
                      id="modeOfPayment"
                      value={personalInfo.modeOfPayment}
                      onChange={handlePersonalInfoChange}
                      name="modeOfPayment"
                    >
                      {!shouldHideFields &&
                        BioSurveillanceValues.includes(customerIdentifier) && (
                          <option value="Biosurveillance">
                            Biosurveillance
                          </option>
                        )}
                      <option value="Credit Card">Credit Card</option>
                      {userPaymentMode !== "Credit Card" && (
                        <option value="GIRO">GIRO</option>
                      )}

                      {!shouldHideFields &&
                        uenValues.includes(customerIdentifier) && (
                          <option value="Exchange of Service agreement">
                            Exchange of Service agreement
                          </option>
                        )}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {!shouldHideFields && (
              <div>
                <div className="row mb-3">
                  <div className="col-lg-6">
                    <div className="row">
                      <div className="col-sm-5">
                        <label htmlFor="paymentBy" className="form-label">
                          Payment by
                        </label>
                      </div>
                      <div className="col-sm-7">
                        <select
                          className="form-select"
                          id="paymentBy"
                          value={personalInfo.paymentBy}
                          onChange={handlePersonalInfoChange}
                          name="paymentBy"
                        >
                          <option value="Applicant">Applicant</option>
                          {/* <option value="Third-party">Third-party</option> */}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="row">
                      <div className="col-sm-5">
                        <label htmlFor="mobileNumber" className="form-label">
                          Mobile Number
                        </label>
                      </div>
                      <div className="col-sm-7">
                        <input
                          type="number"
                          className="form-control"
                          onChange={handleContactChange}
                          name="mobileNumber"
                          value={contact?.mobileNumber}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* {personalInfo.paymentBy === "Third-party" && (
                  <div className="row mb-3">
                    <div className="col-lg-6">
                      <div className="row">
                        <div className="col-sm-5">
                          <label
                            htmlFor="thirdPartyNRIC"
                            className="form-label"
                          >
                            Third-party NRIC/UEN
                          </label>
                        </div>
                        <div className="col-sm-7">
                          <input
                            type="text"
                            className="form-control"
                            id="thirdPartyNRIC"
                            value={personalInfo.thirdPartyNRIC}
                            onChange={handlePersonalInfoChange}
                            name="thirdPartyNRIC"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="row">
                        <div className="col-sm-5">
                          <label
                            htmlFor="thirdPartyEmail"
                            className="form-label"
                          >
                            Third-party Email
                          </label>
                        </div>
                        <div className="col-sm-7">
                          <input
                            type="email"
                            className="form-control"
                            id="thirdPartyEmail"
                            value={personalInfo.thirdPartyEmail}
                            onChange={handlePersonalInfoChange}
                            name="thirdPartyEmail"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            )}
            {personalInfo.customerType === "CorpPass" && (
              <div className="row mb-3">
                <div className="col-lg-12">
                  <div className="row">
                    <div className="col-lg-3">
                      <label
                        htmlFor="selectMailingAddress"
                        className="form-label"
                      >
                        Select Mailing Address
                      </label>
                    </div>
                    <div className="col-sm-9">
                      <select
                        className="form-select"
                        id="selectMailingAddress"
                        onChange={handleSelectChange}
                        value={
                          isNewAddress
                            ? "new"
                            : appDataAddressList.findIndex(
                                (addr) =>
                                  JSON.stringify(addr) ===
                                  JSON.stringify(address)
                              )
                        }
                      >
                        <option value="">Select an address</option>
                        {appDataAddressList.map((addr, index) => (
                          <option key={index} value={index}>
                            {formatAddress(addr)}
                          </option>
                        ))}
                        <option value="new">Add New Address</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row mb-3">
              <div className="col-lg-12">
                {personalInfo.customerType !== "CorpPass" && (
                  <div className="col-lg-6">
                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="editMailingAddress"
                        checked={isNewAddress}
                        onChange={handleCheckboxChangeEditMailingAddress}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="editMailingAddress"
                      >
                        Edit mailing address (for this application only)
                      </label>
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col-lg-3">
                    <label htmlFor="address" className="form-label">
                      Mailing Address
                    </label>
                  </div>
                  <div className="col-sm-9">
                    <div className="row">
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Block No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="blockHouseNo"
                          value={address?.blockHouseNo}
                          maxLength={10}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                      </div>

                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Street Name<span className="star-red">*</span>
                        </label>

                        <input
                          type="text"
                          className={`form-control ${
                            mailingErrors?.streetName ? "is-invalid" : ""
                          }`}
                          name="streetName"
                          value={address?.streetName}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                        {mailingErrors?.streetName && (
                          <div className="invalid-feedback">
                            {mailingErrors.streetName}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Floor Level
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          name="floorLevel"
                          maxLength={10}
                          value={address?.floorLevel}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Unit
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="unit"
                          maxLength={10}
                          value={address?.unit}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                      </div>
                      {/*{address?.buildingName && (*/}
                        <div className="col-md-12 pos-rel mt-2">
                          <label className="col-form-label" id="label-design">
                            Building Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="buildingName"
                            value={address?.buildingName}
                            onChange={handleAddressChange}
                            disabled={!isNewAddress}
                          />
                        </div>
                      {/*)}*/}
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Country
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            mailingErrors?.country ? "is-invalid" : ""
                          }`}
                          name="country"
                          value={address?.country}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Postal Code<span className="star-red">*</span>
                        </label>

                        <input
                          type="text"
                          className={`form-control ${
                            mailingErrors?.postalCode ? "is-invalid" : ""
                          }`}
                          name="postalCode"
                          value={address?.postalCode}
                          maxLength={10}
                          onChange={handleAddressChange}
                          disabled={!isNewAddress}
                        />
                        {mailingErrors?.postalCode && (
                          <div className="invalid-feedback">
                            {mailingErrors.postalCode}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-lg-12">
                <div className="col-lg-6">
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="SameAsMailingAddress"
                      name="SameAsMailingAddress"
                      checked={personalInfo.SameAsMailingAddress}
                      onChange={handlePersonalInfoChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="SameAsMailingAddress"
                    >
                      Billing address same as mailing address
                    </label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-3">
                    <div>
                      <label htmlFor="address" className="form-label">
                        Billing Address
                      </label>
                    </div>
                  </div>
                  <div className="col-md-9">
                    <div className="row">
                      <div className="col-md-4 pos-rel">
                        <label className="col-form-label" id="label-design">
                          Block No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="blockHouseNo"
                          value={billingAddress?.blockHouseNo}
                          maxLength={10}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>

                      <div className="col-md-8 pos-rel">
                        <label className="col-form-label" id="label-design">
                          Street Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="streetName"
                          value={billingAddress?.streetName}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Floor Level
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="floorLevel"
                          maxLength={10}
                          value={billingAddress?.floorLevel}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Unit
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          name="unit"
                          maxLength={10}
                          value={billingAddress?.unit}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>
                      {/*{billingAddress?.buildingName && (*/}
                        <div className="col-md-12 pos-rel mt-2">
                          <label className="col-form-label" id="label-design">
                            Building Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="buildingName"
                            value={billingAddress?.buildingName}
                            onChange={handleBillingAddressChange}
                            disabled={SameAsMailingAddress}
                          />
                        </div>
                      {/*)}*/}
                      <div className="col-md-4 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Country
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          name="country"
                          value={billingAddress?.country}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>
                      <div className="col-md-8 pos-rel mt-2">
                        <label className="col-form-label" id="label-design">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="postalCode"
                          maxLength={10}
                          value={billingAddress?.postalCode}
                          onChange={handleBillingAddressChange}
                          disabled={SameAsMailingAddress}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PersonalInformation;
